import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { Check, Loader2, ShieldCheck, Sparkles } from "lucide-react";
import { toast } from "sonner";
import { Container } from "@/components/layout/Container";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { useClasses } from "@/hooks/useCurriculum";
import { useCurrentUser } from "@/hooks/useCurrentUser";
import {
  useInitiateSubscription,
  useMySubscriptions,
  useVerifySubscription,
} from "@/hooks/useSubscription";
import { useAuth } from "@/hooks/useAuth";
import { formatRupees, formatDate } from "@/lib/format";
import {
  brandHex,
  loadRazorpayCheckout,
  paymentsEnabled,
  type RazorpayHandlerResponse,
} from "@/lib/razorpay";
import type { ClassId } from "@/lib/api-types";

// Display-only pricing. Razorpay Checkout always receives `order.amount` from
// the backend; none of these reference/value calculations determine a charge.
const PRICING_DISPLAY = (() => {
  const referenceRupees = 4999;
  const actualRupees = 2999;
  return Object.freeze({
    referenceRupees,
    actualRupees,
    savingRupees: referenceRupees - actualRupees,
    discountPercent: Math.round(
      ((referenceRupees - actualRupees) / referenceRupees) * 100,
    ),
    monthlyEquivalentRupees: Math.round(actualRupees / 12),
  });
})();

const PERKS = [
  "Every chapter, every subject for your class",
  "Notes, slides, flashcards, mind maps & quizzes",
  "AI features are subject to fair-use limits",
  "Progress tracking & streaks",
];

export function SubscribePage() {
  const navigate = useNavigate();
  const { email } = useAuth();
  const classesQuery = useClasses();
  const me = useCurrentUser();
  const mySubs = useMySubscriptions();
  const initiate = useInitiateSubscription();
  const verify = useVerifySubscription();

  const [processing, setProcessing] = useState(false);

  const enabled = paymentsEnabled();

  // Class is LOCKED to the one chosen during onboarding -- a per-grade
  // subscription can't be switched. To change it (pre-purchase) the user goes
  // back to /onboarding?edit=1.
  const effectiveClass = isClassId(me.data?.class_id) ? me.data.class_id : null;

  const classes = classesQuery.data ?? [];
  const activeSub = mySubs.data?.active ?? null;
  const lockedClassName =
    classes.find((c) => c.class_id === effectiveClass)?.class_name ??
    (effectiveClass ? classLabel(effectiveClass) : null);

  async function handleConfirm() {
    if (!effectiveClass) {
      toast.error("Pick your class first.");
      return;
    }
    if (!enabled) return; // gated by the disabled-state UI below

    setProcessing(true);
    try {
      const order = await initiate.mutateAsync({
        class_id: effectiveClass,
      });

      const Razorpay = await loadRazorpayCheckout();

      const rzp = new Razorpay({
        key: order.key_id,
        amount: order.amount,
        currency: order.currency,
        name: "StaxED",
        description: `StaxED Annual Plan — ${lockedClassName ?? classLabel(order.class_id)}`,
        order_id: order.razorpay_order_id,
        prefill: {
          name: me.data?.user_name ?? undefined,
          email: email ?? me.data?.email ?? undefined,
          contact: me.data?.phone ?? undefined,
        },
        theme: { color: brandHex() },
        handler: async (response: RazorpayHandlerResponse) => {
          try {
            await verify.mutateAsync({
              razorpay_order_id: response.razorpay_order_id,
              razorpay_payment_id: response.razorpay_payment_id,
              razorpay_signature: response.razorpay_signature,
            });
            // Refresh the cached user so the rest of the app sees the new sub.
            await me.refetch();
            navigate("/subscribe/success", {
              replace: true,
              state: { classId: order.class_id, plan: order.plan },
            });
          } catch {
            toast.error("We couldn't confirm your payment. If you were charged, contact support and we'll sort it out.");
          } finally {
            setProcessing(false);
          }
        },
        modal: {
          ondismiss: () => {
            setProcessing(false);
            toast.message("Payment cancelled -- you weren't charged.");
          },
        },
      });

      rzp.on("payment.failed", (resp) => {
        setProcessing(false);
        toast.error(resp.error?.description || "Payment failed. Please try again.");
      });

      rzp.open();
    } catch (err) {
      setProcessing(false);
      const message =
        (err as { response?: { data?: { status_message?: string } } }).response?.data
          ?.status_message ||
        (err as { message?: string }).message ||
        "Something went wrong starting your payment.";
      toast.error(message);
    }
  }

  // Already subscribed -- send them to billing rather than re-charging.
  if (activeSub) {
    return (
      <Container size="sm" className="py-12 lg:py-16">
        <Card className="p-6 sm:p-8 text-center">
          <div className="mx-auto flex h-12 w-12 items-center justify-center rounded-full bg-success-soft">
            <ShieldCheck className="h-6 w-6 text-success" />
          </div>
          <h1 className="mt-4 font-display text-2xl font-semibold">You're already subscribed</h1>
          <p className="mt-2 text-text-muted">
            Your Annual Plan for {classLabel(activeSub.class)} is active until{" "}
            {formatDate(activeSub.end_date)}.
          </p>
          <div className="mt-6 flex flex-col sm:flex-row gap-2 justify-center">
            <Button onClick={() => navigate("/subjects")}>Start studying</Button>
            <Button variant="secondary" onClick={() => navigate("/billing")}>
              View billing
            </Button>
          </div>
        </Card>
      </Container>
    );
  }

  return (
    <Container size="md" className="py-8 sm:py-12 lg:py-16">
      <div className="mx-auto max-w-2xl">
        <div className="text-center">
          <Badge tone="brand" className="mx-auto">
            <Sparkles className="h-3.5 w-3.5" />
            StaxED Premium
          </Badge>
          <h1 className="mt-3 font-display text-3xl sm:text-4xl font-bold tracking-tight">
            Unlock your full curriculum
          </h1>
          <p className="mt-2 text-text-muted">
            One annual payment, everything for your class. No auto-renewal.
          </p>
        </div>

        {/* Perks ----------------------------------------------------------- */}
        <Card className="mt-8 p-5 sm:p-6">
          <ul className="grid gap-3 sm:grid-cols-2">
            {PERKS.map((perk) => (
              <li key={perk} className="flex items-start gap-2 text-sm">
                <Check className="mt-0.5 h-4 w-4 shrink-0 text-success" />
                <span>{perk}</span>
              </li>
            ))}
          </ul>
        </Card>

        {/* Class (locked to onboarding choice) ----------------------------- */}
        <section className="mt-8">
          <h2 className="text-sm font-medium mb-3">Your class</h2>
          {!effectiveClass ? (
            <Card className="p-5 flex flex-col sm:flex-row sm:items-center gap-3">
              <p className="flex-1 text-sm text-text-muted">
                Pick your class first so we can set up the right curriculum.
              </p>
              <Button variant="secondary" size="sm" onClick={() => navigate("/onboarding?edit=1")}>
                Choose class
              </Button>
            </Card>
          ) : (
            <Card className="p-5 flex items-center justify-between gap-3">
              <div className="flex items-center gap-3">
                <div className="grid h-10 w-10 place-items-center rounded-lg bg-brand-soft text-brand font-display font-semibold">
                  {lockedClassName?.replace(/^Class\s*/i, "") ?? "-"}
                </div>
                <div>
                  <p className="font-medium">{lockedClassName}</p>
                  <p className="text-xs text-text-muted">Subscription is tied to this class.</p>
                </div>
              </div>
              <button
                type="button"
                onClick={() => navigate("/onboarding?edit=1")}
                className="text-sm font-medium text-brand hover:underline shrink-0"
              >
                Change class
              </button>
            </Card>
          )}
        </section>

        {/* Annual plan ----------------------------------------------------- */}
        <section className="mt-8">
          <h2 className="text-sm font-medium mb-3">Your plan</h2>
          <Card className="relative overflow-hidden border-brand p-5 ring-2 ring-ring/20 sm:p-6">
            <Badge tone="accent" className="absolute right-4 top-4">
              {PRICING_DISPLAY.discountPercent}% off
            </Badge>
            <p className="font-display text-lg font-semibold">Annual Plan</p>
            <div className="mt-4 flex flex-wrap items-end gap-x-3 gap-y-1">
              <del
                aria-label={`Original reference price ${formatRupees(PRICING_DISPLAY.referenceRupees)}`}
                className="pb-1 text-lg text-text-subtle decoration-2"
              >
                {formatRupees(PRICING_DISPLAY.referenceRupees)}
              </del>
              <span className="font-display text-4xl font-bold tracking-tight text-text">
                {formatRupees(PRICING_DISPLAY.actualRupees)}
              </span>
              <span className="pb-1 text-sm text-text-muted">for 12 months</span>
            </div>
            <p className="mt-2 font-medium text-success">
              Save {formatRupees(PRICING_DISPLAY.savingRupees)}
            </p>
            <div className="mt-4 rounded-lg bg-surface-subtle p-4 text-sm">
              <p className="font-medium">
                Works out to approximately{" "}
                {formatRupees(PRICING_DISPLAY.monthlyEquivalentRupees)}/month.
              </p>
              <p className="mt-1 text-text-muted">
                Billed as one {formatRupees(PRICING_DISPLAY.actualRupees)} upfront payment
                for 12 months. There is no monthly payment plan.
              </p>
            </div>
            <ul className="mt-4 space-y-2 text-sm text-text-muted">
              <li className="flex items-center gap-2">
                <Check className="h-4 w-4 text-success" />
                12 months of access
              </li>
              <li className="flex items-center gap-2">
                <Check className="h-4 w-4 text-success" />
                One payment. No auto-renewal.
              </li>
              <li className="flex items-center gap-2">
                <Check className="h-4 w-4 text-success" />
                AI features are subject to fair-use limits.
              </li>
            </ul>
          </Card>
        </section>

        {/* Confirm / disabled state --------------------------------------- */}
        <div className="mt-8">
          {enabled ? (
            <>
              <Button
                fullWidth
                size="lg"
                onClick={handleConfirm}
                disabled={!effectiveClass || processing}
              >
                {processing ? (
                  <>
                    <Loader2 className="h-4 w-4 animate-spin" />
                    Opening secure checkout...
                  </>
                ) : (
                  <>
                    Continue — pay {formatRupees(PRICING_DISPLAY.actualRupees)} upfront
                  </>
                )}
              </Button>
              <p className="mt-3 flex items-center justify-center gap-1.5 text-xs text-text-muted">
                <ShieldCheck className="h-3.5 w-3.5" />
                Secure payment via Razorpay. We never store your card details.
              </p>
            </>
          ) : (
            <motion.div
              initial={{ opacity: 0, y: 6 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.3 }}
            >
              <Card className="p-6 text-center">
                <h3 className="font-display text-lg font-semibold">
                  Payments aren't enabled yet
                </h3>
                <p className="mt-2 text-sm text-text-muted">
                  We're putting the finishing touches on checkout. Check back soon -- your
                  class choice will be waiting.
                </p>
                <Button
                  variant="secondary"
                  className="mt-5"
                  onClick={() => navigate("/subjects")}
                >
                  Browse free chapters
                </Button>
              </Card>
            </motion.div>
          )}
        </div>
      </div>
    </Container>
  );
}

// "class-11" -> "Class 11" fallback when we don't have the curriculum record.
function classLabel(classId: string): string {
  const n = classId.replace(/^class-/, "");
  return /^\d+$/.test(n) ? `Class ${n}` : classId;
}

function isClassId(value: string | null | undefined): value is ClassId {
  return ["class-8", "class-9", "class-10", "class-11", "class-12"].includes(value ?? "");
}
